import { ApolloLink, from, type Operation } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { type OperationDefinitionNode, type OperationTypeNode } from 'graphql'

export type LogEnvironment = 'server' | 'client' | 'both' | 'none'

interface LoggerOptions {
  /**
   * Indicates whether to log GraphQL client operations on 'server', 'client', 'both', or 'none' (default none)
   * Note that error logs are always logged on both server and client
   */
  logOn?: LogEnvironment
  /**
   * Indicates whether to log the results of operations on 'server', 'client', 'both', or 'none' (default none)
   */
  logResultOn?: LogEnvironment
}

interface OperationInfo {
  /**
   * Schema name
   */
  schemaName: string
  /**
   * Operation type (query, mutation, subscription)
   */
  operationType: OperationTypeNode
  /**
   * Operation name
   */
  operationName: string
}

/**
 * Gets operation info for logging
 */
function getOperationInfo (operation: Operation): OperationInfo {
  return {
    schemaName: operation.getContext().contentstackLink === true ? ' contentstack' : '',
    operationType: (operation.query.definitions[0] as OperationDefinitionNode).operation,
    operationName: operation.operationName ?? 'Unnamed Operation'
  }
}

/**
 * Converts a JSON object to a string with a line limit (for server logging)
 */
function jsonToString (obj: Record<string, any>): string {
  const result = JSON.stringify(obj, null, 2)

  // Split into lines and return the first 100 lines if needed
  const lines = result.split('\n')
  if (lines.length > 100) {
    return lines.slice(0, 100).join('\n') + '...'
  }

  return result
}

/**
 * Logs Apollo client operations to the console
 */
function loggerLink ({
  logOn = 'none',
  logResultOn = 'none'
}: LoggerOptions): ApolloLink {
  const links: ApolloLink[] = []

  /**
   * Logging for operations and their results if successful (only logged when configured)
   */
  if (['server', 'client', 'both'].includes(logOn)) {
    links.push(
      new ApolloLink((operation, forward) => {
        const isServer = typeof window === 'undefined'

        // Skip logging if needed
        if ((logOn === 'client' && isServer) || (logOn === 'server' && !isServer)) {
          return forward(operation)
        }

        // Log the operation
        return forward(operation).map((result) => {
          const { schemaName, operationType, operationName } = getOperationInfo(operation)

          console.groupCollapsed(`[GraphQL${schemaName} ${operationType}] ${operationName}`)
          if (Object.keys(operation.variables).length > 0) {
            console.log('variables:', isServer ? jsonToString(operation.variables) : operation.variables)
          }
          if (logResultOn === 'both' || (logResultOn === 'client' && !isServer) || (logResultOn === 'server' && isServer)) {
            console.log('result:', isServer ? jsonToString(result) : result)
          }
          console.groupEnd()

          return result
        })
      })
    )
  }

  /**
   * Logging for errors (always logged)
   */
  links.push(
    onError(({ operation, graphQLErrors, networkError }) => {
      const { schemaName, operationType, operationName } = getOperationInfo(operation)

      if (graphQLErrors != null) {
        if (Array.isArray(graphQLErrors)) {
          for (const graphQLError of graphQLErrors) {
            console.error(`[GraphQL${schemaName} ${operationType} error]: ${operationName} ${graphQLError.message}`, graphQLError)
          }
        } else {
          console.error(`[GraphQL${schemaName} ${operationType} error]: ${operationName} ${JSON.stringify(graphQLErrors)}`, graphQLErrors)
        }
      }

      if (networkError != null) {
        console.error(`[GraphQL${schemaName} ${operationType} network error]: ${operationName} ${networkError.message}`, networkError)
      }
    })
  )

  return from(links)
}

export {
  loggerLink
}
